import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { shortTitle, shortDescription } from "@/utils/metaLength";
import { getLangFromUrl, setLangInUrl, useHreflang, updateCanonical, useAutoSyncLangToUrl } from "@/utils/seoLang";
import axios from "axios";
import { toast } from "sonner";
import { CITIES } from "@/cityConfig";
import { convertPrice, getCurrencyByCode } from "@/currencyConfig";
import { trackConversion } from "@/utils/analytics";
import { trackPageView, trackClick } from "@/utils/tracker";
import { t } from "@/translations";

import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import CategoriesSection from "@/components/CategoriesSection";
import ProductsSection from "@/components/ProductsSection";
import AdvantagesSection from "@/components/AdvantagesSection";
import HowWeWorkSection from "@/components/HowWeWorkSection";
import ReviewsSection from "@/components/ReviewsSection";
import CityReviews from "@/components/CityReviews";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import BouquetLeadForm from "@/components/BouquetLeadForm";
import ProductPreview from "@/components/ProductPreview";

import FAQSection from "@/components/FAQSection";
import SeoTextSection from "@/components/SeoTextSection";
import WeddingsBanner from "@/components/WeddingsBanner";
import InstagramWidget from "@/components/InstagramWidget";
import GuaranteesSection from "@/components/GuaranteesSection";
import BearsSection from "@/components/BearsSection";
import { CityJsonLd } from "@/components/JsonLd";
import CityBlogSection from "@/components/CityBlogSection";
import RelatedGuidesSection from "@/components/RelatedGuidesSection";
import RelatedCityArticles from "@/components/RelatedCityArticles";
import NearbyCitiesLinks from "@/components/NearbyCitiesLinks";
import CycladesNearbyBlock from "@/components/CycladesNearbyBlock";
import AsiaNearbyBlock from "@/components/AsiaNearbyBlock";
import GoogleReviewCta from "@/components/GoogleReviewCta";

const DEFAULT_LANGS = ["ru", "en"];

const API = "/api";

export default function CityPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const city = CITIES[citySlug];

  const availableLanguages = city?.languages || DEFAULT_LANGS;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortOrder, setSortOrder] = useState("asc");
  const [previewProduct, setPreviewProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [lang, setLangState] = useState(() => getLangFromUrl(searchParams, availableLanguages));

  const setLang = (newLang) => {
    setLangState(newLang);
    setLangInUrl(newLang, searchParams, setSearchParams);
  };

  // If lang was auto-detected (from localStorage or navigator), reflect it in URL
  useAutoSyncLangToUrl(lang, searchParams, setSearchParams);

  // hreflang tags for SEO
  useHreflang(`/${citySlug}`, availableLanguages);

  // Redirect if city doesn't exist
  useEffect(() => {
    if (!city) {
      navigate("/antalya", { replace: true });
    }
  }, [city, navigate]);

  // SEO: update document title and meta based on language
  const heroImage = "https://yuccaflower.com/hero.webp";

  useEffect(() => {
    if (!city) return;
    const seo = city.seo[lang] || city.seo.ru;
    document.title = shortTitle(seo.title);
    // Standard meta
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", shortDescription(seo.description));
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) metaKeywords.setAttribute("content", seo.keywords);
    // Open Graph
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", seo.title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", seo.description);
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) ogImage.setAttribute("content", heroImage);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", `https://yuccaflower.com/${citySlug}`);
    // Twitter
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute("content", seo.title);
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute("content", seo.description);
    const twImage = document.querySelector('meta[name="twitter:image"]');
    if (twImage) twImage.setAttribute("content", heroImage);
    // Canonical
    updateCanonical(`/${citySlug}`, availableLanguages);
    document.documentElement.lang = lang;
  }, [city, lang, citySlug, availableLanguages]);

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes, settingsRes, ratesRes] = await Promise.all([
        axios.get(`${API}/products?city=${citySlug}`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/exchange-rates`),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setSettings(settingsRes.data);
      setExchangeRates(ratesRes.data.rates);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  }, [citySlug]);

  useEffect(() => {
    setLoading(true);
    if (city) setSelectedCurrency(city.currency);
    fetchData();
    // Track page view
    if (citySlug) trackPageView(`/${citySlug}`, citySlug);
  }, [fetchData, city, citySlug]);

  const handleCityChange = (slug) => {
    navigate(`/${slug}`);
  };

  const filteredProducts = (() => {
    const sorted = products.slice().sort((a, b) => sortOrder === "asc" ? a.price - b.price : b.price - a.price);
    if (selectedCategory !== "all") {
      return sorted.filter(p => p.category === selectedCategory);
    }
    // "All" mode: cheapest first (asc) or just sorted (desc), then alternate standard/medium, premium last
    if (sortOrder === "desc") {
      return sorted;
    }
    const cheapest = sorted[0];
    const rest = sorted.slice(1);
    const standards = rest.filter(p => p.category === "standard");
    const mediums = rest.filter(p => p.category === "medium");
    const premiums = rest.filter(p => p.category === "premium");
    const interleaved = [];
    let si = 0, mi = 0;
    while (si < standards.length || mi < mediums.length) {
      if (si < standards.length) interleaved.push(standards[si++]);
      if (mi < mediums.length) interleaved.push(mediums[mi++]);
    }
    return cheapest ? [cheapest, ...interleaved, ...premiums] : [...interleaved, ...premiums];
  })();

  // City name helpers based on language
  const cityPrep = lang === "en" ? city.namePrepositionalEn : (lang === "tr" ? (city.namePrepositionalTr || city.namePrepositionalEn) : city.namePrepositional);
  const cityName = lang === "en" ? city.nameEn : (lang === "tr" ? (city.nameTr || city.nameEn) : city.name);

  // Currency conversion
  const baseCurrency = city.currency;
  const displayCurrency = getCurrencyByCode(selectedCurrency || baseCurrency);
  const cp = (price) => convertPrice(price, baseCurrency, displayCurrency.code, exchangeRates);

  const openWhatsApp = (product = null) => {
    const phone = settings?.whatsapp?.replace(/[^0-9]/g, "") || "905316224378";
    let message;
    if (product) {
      const prodName = lang === "tr" && product.name_tr ? product.name_tr : (lang === "en" && product.name_en ? product.name_en : product.name);
      if (product.category === "premium") {
        message = lang === "tr"
          ? `Merhaba! "${prodName}" icin fiyat ogrenmek istiyorum (${cityName})`
          : lang === "en"
          ? `Hello! I'd like to know the price for "${prodName}" (${cityName})`
          : `Здравствуйте! Хочу узнать цену "${prodName}" (${cityName})`;
      } else if (product.category === "bears") {
        const priceStr = `${displayCurrency.symbol}${cp(product.price)}`;
        message = lang === "tr"
          ? `Merhaba! "${prodName}" pelus ayicigi siparis etmek istiyorum (${priceStr}, ${cityName})`
          : lang === "en"
          ? `Hello! I'd like to order the "${prodName}" plush bear (${priceStr}, ${cityName})`
          : `Здравствуйте! Хочу заказать плюшевого мишку "${prodName}" (${priceStr}, ${cityName})`;
      } else {
        message = t("wa.order", lang, { name: prodName, price: `${displayCurrency.symbol}${cp(product.price)}`, city: cityName });
      }
    } else {
      message = t("wa.general", lang, { city: cityPrep });
    }
    trackConversion("whatsapp", product?.name || null);
    trackClick("whatsapp", citySlug);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank");
  };

  if (!city) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#e8b4b8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        settings={settings}
        currentCity={city}
        onCityChange={handleCityChange}
        selectedCurrency={selectedCurrency || baseCurrency}
        onCurrencyChange={setSelectedCurrency}
        lang={lang}
        onLangChange={setLang}
        t={(key, vars) => t(key, lang, vars)}
        availableLanguages={availableLanguages}
      />

      <main>
        <HeroSection
          city={city}
          onOrderClick={() => openWhatsApp()}
          onWhatsAppClick={() => openWhatsApp()}
          lang={lang}
          t={(key, vars) => t(key, lang, vars)}
        />

        <CategoriesSection
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          sortOrder={sortOrder}
          onSortChange={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")}
          currencySymbol={displayCurrency.symbol}
          lang={lang}
          t={(key, vars) => t(key, lang, vars)}
        />

        <ProductsSection
          products={filteredProducts}
          onOrderClick={openWhatsApp}
          onImageClick={setPreviewProduct}
          cityName={cityPrep}
          currencySymbol={displayCurrency.symbol}
          convertPrice={cp}
          lang={lang}
          t={(key, vars) => t(key, lang, vars)}
        />

        <BouquetLeadForm city={citySlug} lang={lang} variant="section" />

        <BearsSection
          citySlug={citySlug}
          cityName={cityName}
          lang={lang}
          onOrderClick={openWhatsApp}
          currencySymbol={displayCurrency.symbol}
          convertPrice={cp}
        />

        <HowWeWorkSection lang={lang} t={(key, vars) => t(key, lang, vars)} />

        <AdvantagesSection lang={lang} t={(key, vars) => t(key, lang, vars)} />

        <GuaranteesSection lang={lang} />

        <ReviewsSection lang={lang} t={(key, vars) => t(key, lang, vars)} id="chat-reviews" />

        <CityReviews city={citySlug} lang={lang} allReviewsHref={`/${citySlug}/reviews${lang !== "ru" ? `?lang=${lang}` : ""}`} />

        <GoogleReviewCta url={city.googleReviewUrl} lang={lang} />

        <InstagramWidget lang={lang} />

        <CityBlogSection citySlug={citySlug} lang={lang} />

        <RelatedGuidesSection lang={lang} />

        <RelatedCityArticles citySlug={citySlug} lang={lang} />

        <CycladesNearbyBlock citySlug={citySlug} lang={lang} />

        <AsiaNearbyBlock citySlug={citySlug} lang={lang} />

        <NearbyCitiesLinks citySlug={citySlug} lang={lang} />

        <FAQSection citySlug={citySlug} lang={lang} t={(key, vars) => t(key, lang, vars)} />

        <WeddingsBanner citySlug={citySlug} lang={lang} />

        {city.districts && city.districts.length > 0 && (
          <section className="py-12 sm:py-16 bg-white" data-testid="districts-section">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "Playfair Display, serif" }}>
                  {lang === "tr" ? "Bolgelere teslimat" : lang === "en" ? "Delivery by area" : "Доставка по районам"}
                </h2>
                <p className="text-gray-500 text-sm sm:text-base mt-2">
                  {lang === "tr"
                    ? `${city.nameTr || city.nameEn} kiyisinin tum bolgelerine ve otellerine teslimat`
                    : lang === "en"
                    ? `Delivery to all areas and hotels along the ${city.nameEn} coast`
                    : `Доставляем по всему побережью ${city.namePrepositional.replace(/^в /, "")} и в любые отели`}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {city.districts.map((d) => {
                  const dName = lang === "en" ? d.nameEn : lang === "tr" ? d.nameTr || d.nameEn : d.name;
                  const dSeo = d.seo[lang] || d.seo.ru;
                  const href = `/${citySlug}/${d.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`;
                  return (
                    <a
                      key={d.slug}
                      href={href}
                      className="group block p-5 rounded-2xl border border-gray-100 hover:border-pink-200 hover:shadow-md transition-all bg-gradient-to-br from-white to-pink-50/30"
                      data-testid={`district-link-${d.slug}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-lg group-hover:text-pink-600 transition-colors">
                            {dName}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-500 mt-1 line-clamp-2">
                            {dSeo.description?.split(".")[0]}
                          </p>
                        </div>
                        <span className="text-pink-500 text-lg shrink-0 group-hover:translate-x-1 transition-transform">→</span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <SeoTextSection citySlug={citySlug} lang={lang} />
      </main>

      <Footer settings={settings} city={city} lang={lang} t={(key, vars) => t(key, lang, vars)} />

      <CityJsonLd citySlug={citySlug} cityName={city.name} lang={lang} products={products} />

      <FloatingWhatsApp onClick={() => openWhatsApp()} />

      {previewProduct && (
        <ProductPreview
          product={previewProduct}
          onClose={() => setPreviewProduct(null)}
          onOrderClick={openWhatsApp}
          currencySymbol={displayCurrency.symbol}
          convertPrice={cp}
          lang={lang}
          t={(key, vars) => t(key, lang, vars)}
        />
      )}
    </>
  );
}
